import {useCallback, useEffect, useMemo, useRef} from 'react';
import ChartLayout from '../../components/layouts/ChartLayout';
import {Col, Row} from 'react-bootstrap';
import {LineChart} from '@mui/x-charts';
import {EnhancedDashboardReducer, InitialState} from './stores/dashboardReducer';
import {FmlxButton, FmlxComboBox} from 'fmlx-common-ui';
import {ChartColors as colors, DashboardActionTypes} from '../../const/dataEnum';
import Legend from '../../components/ChartLegend/ChartLegend';
import {useDashboardContext} from '../../lib/contexLib';

import {getLastThreePeriods} from '../../api/rest/dashboardApi';

const EmployeeDashboardChart = () => {
  const {dashboardState,dispatchDashboard} = useDashboardContext();

  const chartRef = useRef(null);

  const {
    selectedMultipleQuestions,
    questionCategory,
    questionFilters,
    groupedData,
    selectedMenu,
    xData,
    newDataSeries,
 } = dashboardState;

  const comboBoxItems = [{id: 'all', text: 'All'}, ...questionFilters];
  const selectedCategory = questionCategory.find((category) => category.label === selectedMenu);

  const handleOnButtonClick = useCallback((category) => {
    dispatchDashboard({type: 'SET_SELECTED_MENU', payload: category});

    const filteredSeries = groupedData
      .filter((data) => data.category === category)
      .flatMap((data) => data.series);

    const filteredQuestions = groupedData
      .filter((data) => data.category === category)
      .flatMap((data) =>
        data.series.map((seriesItem, index) => ({
          key: data.index,
          id: `${data.id}-${index}`,
          text: seriesItem.label,
          group: data.text,
       }))
      );

    dispatchDashboard({
      type: 'SET_CHANGED_CATEGORY',
      payload: {filteredSeries, filteredQuestions}
   });
 }, [dispatchDashboard, groupedData]);

  /**
   * PRESENTATION LAYER RENDERING CYCLE
    this useEffect bellow is responsible for rendering the chart "series"
    --- cautions! combining these useEffects with the one above can cause Infinite loops issue
    due to continuesly update state on CategoryScore when fecthing data
   */

  const memoizedDefaultCategories=useMemo(() => questionCategory.length ? questionCategory[0].label : undefined , [questionCategory])

  useEffect(() => {
    const individualOptions = questionFilters.filter(item => item.id !== 'all');
    const allOption = comboBoxItems.find(item => item.id === 'all');
    const isAllIndividualsSelected = individualOptions.length > 0 &&
      individualOptions.every(option => 
        selectedMultipleQuestions.some(item => item.id === option.id)
      );
    const allOptionSelected = selectedMultipleQuestions.some(item => item.id === 'all');
    
    // Set default category when data is loaded
    if (memoizedDefaultCategories && !selectedMenu) {
      handleOnButtonClick(memoizedDefaultCategories);
   }

   if (isAllIndividualsSelected && !allOptionSelected && allOption){
    const updatedSelectedItems = [allOption, ...selectedMultipleQuestions];
    dispatchDashboard({
      type: DashboardActionTypes.SET_SELECTED_QUESTIONS,
      payload: updatedSelectedItems,});
   }
 }, [selectedMenu, handleOnButtonClick, memoizedDefaultCategories, selectedMultipleQuestions, questionFilters, comboBoxItems, dispatchDashboard]);



  const handleOnFilterQuestionChange = (selectedItems) => {
    const isSelectAll = selectedItems.some(item => item.id === 'all');
    const allOption = comboBoxItems.find(item => item.id === 'all');
    const isSelectAllWasSelected = selectedMultipleQuestions.some(item => item.id === 'all');

    let updatedSelectedItems;

    if (isSelectAllWasSelected !== isSelectAll){
      if (isSelectAll) {
        updatedSelectedItems = [allOption, ...questionFilters.filter(item => item.id !== 'all')];
      }else{
        updatedSelectedItems =[];
      }
    } else {
      const individualOptions = questionFilters.filter(item => item.id !== 'all');

      const isAllIndividualsSelected = individualOptions.length > 0 && individualOptions.every(option => 
        selectedItems.some(item => item.id === option.id)
      );

      if (isAllIndividualsSelected && !isSelectAll) {
        updatedSelectedItems = [allOption, ...selectedItems.filter(item => item.id !== 'all')];
      }else if (!isAllIndividualsSelected && isSelectAll){
        updatedSelectedItems = selectedItems.filter(item => item.id !== 'all');
      }else {
        updatedSelectedItems = selectedItems;
      }
    }

    dispatchDashboard({
      type: DashboardActionTypes.SET_SELECTED_QUESTIONS,
      payload: updatedSelectedItems,
   });

    const filteredSeries = groupedData
      .filter(data => data.category === selectedMenu)
      .flatMap(data => data.series)
      .filter(seriesItem =>
        updatedSelectedItems.some(item => item.text === seriesItem.label)
      );

    dispatchDashboard({
      type: DashboardActionTypes.SET_NEW_DATA_SERIES,
      payload: filteredSeries,
   });
 };

 

  const onDeleteQuestionChip = (id) => {
    const isAllOption = id === 'all' || id === 'All' || selectedMultipleQuestions.find(item => item.id === id)?.text === 'All';

    if (isAllOption) {
      dispatchDashboard({ type: DashboardActionTypes.SET_SELECTED_QUESTIONS, payload: [] });
      dispatchDashboard({ type: DashboardActionTypes.SET_NEW_DATA_SERIES, payload: [] });
    }else {
      const updatedQuestions = selectedMultipleQuestions.filter(
        item => item.id !== id && item.id !== 'all');

      dispatchDashboard({ type: DashboardActionTypes.SET_SELECTED_QUESTIONS, payload: updatedQuestions });

      const updatedSeries = groupedData
        .filter((data) => data.category === selectedMenu)
        .flatMap((data) =>
          data.series.filter((seriesItem) =>
            updatedQuestions.some((question) => question.text === seriesItem.label)
          )
        );

      dispatchDashboard({ type: DashboardActionTypes.SET_NEW_DATA_SERIES, payload: updatedSeries });
    }
    
 };

  const handleAxisClick = (event, data) => {
    const axisQuestionLabels = Object.entries(data.seriesValues)
      .filter(([key, value]) => value !== undefined)
      .map(([key]) => {
        const index = parseInt(key.split('-').pop(), 10);
        const seriesItem = newDataSeries[index];
        return seriesItem && seriesItem.label ? seriesItem.label : key;

  //  _____>>>>>  save for future debuging <<<<<_______

        // const mergedLabel = selectedCategory
        //   ? `${selectedCategory.label} - ${seriesLabel}`
        //   : seriesLabel;
        // return {label: mergedLabel, value};
     });

    const newSelectedItems = questionFilters.filter(item =>
      axisQuestionLabels.includes(item.text)
    );

    dispatchDashboard({
      type: DashboardActionTypes.SET_SELECTED_QUESTIONS,
      payload: newSelectedItems,
   });
 };

  const legendItems = useMemo(() => {
    return selectedMultipleQuestions.filter(item => item.id !== 'all');
  }, [selectedMultipleQuestions]);

  return (
    <ChartLayout heading="Performance Review Summary">
      <div className="dashboard-graph-question-filter">
        <div className="question-filter">
          <FmlxComboBox
            multiple={true}
            placeholder="Question Filter"
            items={comboBoxItems}
            selectedMultipleItem={selectedMultipleQuestions}
            onChange={(e) => handleOnFilterQuestionChange(e)}
            onDeleteChip={(e) => onDeleteQuestionChip(e)}
          />
        </div>
        {/* <div className="period-filter">

        >>> saving for future development <<<<<< 

          <FmlxComboBox
            multiple={true}
            placeholder="Period Filter"
            items={periodList}
            selectedMultipleItem={selectedMultipleItems}
            onChange={(e) => handleFilterPeriodChange(e)}
            onDeleteChip={(e) => onDeletePeriodChip(e)}
          />
        </div> */}
      </div>

      {/* Button Group */}
      <div className="d-flex flex-wrap">
        {questionCategory.map((category) => (
          <div key={category.id} className="d-flex flex-wrap p-2">
            <FmlxButton
              size={FmlxButton.Size.MEDIUM}
              label={category.label}
              type={FmlxButton.Type.PRIMARY}
              variant={selectedMenu === category.label ? FmlxButton.Variant.CONTAIN : FmlxButton.Variant.OUTLINE}
              onClick={() => handleOnButtonClick(category.label)}
              selected={selectedMenu === category.label}
            />
          </div>
        ))}
      </div>
      {/* 

          >>> saving for future development <<<<<<

        <FmlxButtonGroup
          size={FmlxButtonGroup.Size.MEDIUM}
          buttons={questionCategory}
          type={(button) => (selectedMenu === button ? FmlxButtonGroup.Type.CONTAIN : FmlxButtonGroup.Type.OUTLINE)}
          variant={FmlxButtonGroup.Variant.BOTH}
          onClick={(e) => handleOnButtonClick(e)}
        /> 
        */}
      {/* Chart and Legend */}
      <Row>
        <Col md={8}>
          <LineChart
            key={JSON.stringify(newDataSeries)}
            slotProps={{legend: {hidden: true}}}
            width={950}
            height={450}
            series={newDataSeries}
            xAxis={[{scaleType: 'point', data: xData}]}
            ref={chartRef}
            grid={{horizontal: true, vertical: true}}
            colors={Object.values(colors)}
            onAxisClick={handleAxisClick}
          />
        </Col>
        <Col md={4}>
          <Legend items={legendItems} colors={colors} selectedCategory={selectedCategory}/>
        </Col>
      </Row>
    </ChartLayout>
  );
};

export default EmployeeDashboardChart;
